import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Editor,
  EditorState,
  RichUtils,
  getDefaultKeyBinding,
  convertToRaw,
  convertFromRaw,
  ContentState,
  CompositeDecorator,
} from "draft-js";
import ToolBarAddNewTask from "./ToolBarAddNewTask";
import "draft-js/dist/Draft.css";
import { convertFromHTML, convertToHTML } from "draft-convert";
import "../css/add-new-task-editor.css";

// --- DRAFT-JS UTILITY COMPONENTS AND FUNCTIONS ---
// These are kept from your original code.
// Link component for rendering links
const Link = (props) => {
  const { url } = props.contentState.getEntity(props.entityKey).getData();
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: "#4a9eff", textDecoration: "underline" }}
    >
      {props.children}
    </a>
  );
};

// Image component for rendering images
const Image = (props) => {
  const { src } = props.contentState.getEntity(props.entityKey).getData();
  return (
    <img
      src={src}
      alt="Uploaded content"
      style={{ maxWidth: "100%", height: "auto" }}
    />
  );
};

// HR component for rendering horizontal rules
const HorizontalRule = () => {
  return <hr style={{ margin: "10px 0", border: "1px solid #ccc" }} />;
};

// Function to find link entities
function findLinkEntities(contentBlock, callback, contentState) {
  contentBlock.findEntityRanges((character) => {
    const entityKey = character.getEntity();
    return (
      entityKey !== null &&
      contentState.getEntity(entityKey).getType() === "LINK"
    );
  }, callback);
}

// Block renderer function for atomic blocks (images, HR)
const blockRendererFn = (block) => {
  if (block.getType() === "atomic") {
    return {
      component: AtomicBlock,
      editable: false,
    };
  }
  return null;
};

// Atomic block component
const AtomicBlock = (props) => {
  const entity = props.contentState.getEntity(props.block.getEntityAt(0));
  const entityType = entity.getType();

  switch (entityType) {
    case "IMAGE":
      return <Image {...props} entityKey={props.block.getEntityAt(0)} />;
    case "HR":
      return <HorizontalRule />;
    default:
      return null;
  }
};

// Create decorator
const decorator = new CompositeDecorator([
  {
    strategy: findLinkEntities,
    component: Link,
  },
]);

// --- REFACTORED MYEDITOR COMPONENT ---
const MyEditor = ({ value, onChange }) => {
  const createEditorStateFromValue = useCallback((val) => {
    if (!val || (typeof val === "string" && val.trim() === "")) {
      return EditorState.createEmpty(decorator);
    }

    try {
      if (typeof val === "object" && val.blocks && val.entityMap) {
        return EditorState.createWithContent(convertFromRaw(val), decorator);
      }
      if (typeof val === "string") {
        try {
          const parsed = JSON.parse(val);
          if (parsed.blocks && parsed.entityMap) {
            return EditorState.createWithContent(
              convertFromRaw(parsed),
              decorator,
            );
          }
        } catch (e) {
          // Not a JSON string, continue to next check.
        }
        if (val.includes("<") && val.includes(">")) {
          const contentState = convertFromHTML({
            htmlToEntity: (nodeName, node, createEntity) => {
              if (nodeName === "a") {
                return createEntity("LINK", "MUTABLE", { url: node.href });
              }
            },
          })(val);
          if (contentState) {
            return EditorState.createWithContent(contentState, decorator);
          }
        }
        return EditorState.createWithContent(
          ContentState.createFromText(val),
          decorator,
        );
      }
    } catch (e) {
      console.warn("Error parsing editor value:", e);
    }
    return EditorState.createEmpty(decorator);
  }, []);

  const [editorState, setEditorState] = useState(() =>
    createEditorStateFromValue(value),
  );

  const editorRef = useRef(null);
  const isInternalChange = useRef(false);

  // This useEffect ensures the editor's state stays in sync with the value prop.
  // It skips syncing when the change came from the editor itself (user typing).
  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    const newEditorState = createEditorStateFromValue(value);
    const currentContentState = editorState.getCurrentContent();
    const newContentState = newEditorState.getCurrentContent();

    // Only update state if the content has actually changed to prevent infinite loops.
    if (
      convertToRaw(currentContentState)
        .blocks.map((b) => b.text)
        .join("") !==
      convertToRaw(newContentState)
        .blocks.map((b) => b.text)
        .join("")
    ) {
      setEditorState(newEditorState);
    }
  }, [value, createEditorStateFromValue]);

  const handleChange = useCallback(
    (newEditorState) => {
      setEditorState(newEditorState);
      if (onChange) {
        isInternalChange.current = true;
        const contentState = newEditorState.getCurrentContent();
        const html = convertToHTML({
          blockToHTML: (block) => {
            if (block.type === "unstyled" && block.text.length === 0) {
              return (
                <p>
                  <br />
                </p>
              );
            }
            return undefined;
          },
          entityToHTML: (entity, originalText) => {
            if (entity.type === "LINK") {
              return `<a href="${entity.data.url}" target="_blank" rel="noopener noreferrer">${originalText}</a>`;
            }
            return originalText;
          },
        })(contentState);
        onChange(html);
      }
    },
    [onChange],
  );

  const handleKeyCommand = useCallback(
    (command, editorState) => {
      const newState = RichUtils.handleKeyCommand(editorState, command);
      if (newState) {
        handleChange(newState);
        return "handled";
      }
      return "not-handled";
    },
    [handleChange],
  );

  const mapKeyToEditorCommand = useCallback((e) => {
    return getDefaultKeyBinding(e);
  }, []);

  const focusEditor = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.focus();
    }
  }, []);

  return (
    <div>
      <ToolBarAddNewTask
        editorState={editorState}
        onChange={handleChange}
        editorRef={editorRef}
      />

      <div
        onClick={focusEditor}
        style={{
          color: "white",
          border: "1px solid #273b4b",
          minHeight: "70px",
          maxHeight: "200px",
          padding: "10px",
          borderBottomLeftRadius: "12px",
          borderBottomRightRadius: "12px",
          overflowY: "auto",
          cursor: "text",
          textAlign: "left", // Force left alignment
          direction: "ltr", // Force left-to-right direction
        }}
      >
        <Editor
          editorState={editorState}
          onChange={handleChange}
          handleKeyCommand={handleKeyCommand}
          keyBindingFn={mapKeyToEditorCommand}
          ref={editorRef}
          spellCheck={true}
          blockRendererFn={blockRendererFn}
        />
      </div>
    </div>
  );
};

export default MyEditor;
